/** DLP の一覧・パネルで共通に使う小さな表示(札・端末・従業員・単位)。選択部品は selects.tsx。 */
import { Tag, Typography } from "antd";
import { Link } from "react-router-dom";

import type { DlpDeviceBrief, DlpScan, EmployeeBrief } from "../../lib/api";
import { StatusTag } from "../../ui";
import {
  browserPolicyLabel,
  browserPolicyTone,
  scanStatusLabel,
  scanStatusTone,
  triggerLabel,
  uploadActionLabel,
  uploadActionTone,
  uploadAttributionLabel,
  uploadEventTone,
  uploadKindLabel,
  uploadStateLabel,
  uploadStateTone,
  usbKindLabel,
  usbKindTone,
  usbModeLabel,
  usbModeTone,
} from "./constants";

/** スキャンの状態札。done は検出件数で色が変わる。 */
export function scanStatusTag(s: Pick<DlpScan, "status" | "findings_count">) {
  return (
    <StatusTag tone={scanStatusTone(s.status, s.findings_count)} dot>
      {scanStatusLabel(s.status)}
    </StatusTag>
  );
}

/** 契機(退職 / 手動)は分類なので antd Tag。 */
export function triggerTag(v: string) {
  return <Tag color={v === "offboard" ? "purple" : "default"}>{triggerLabel(v)}</Tag>;
}

export function usbKindTag(kind: string) {
  return (
    <StatusTag tone={usbKindTone(kind)} dot>
      {usbKindLabel(kind)}
    </StatusTag>
  );
}

export function usbModeTag(mode: string | null | undefined) {
  return <StatusTag tone={usbModeTone(mode)}>{usbModeLabel(mode)}</StatusTag>;
}

/** アップロード検知の種類札(tone は種類 × 帰属) */
export function uploadKindTag(kind: string, attribution: string | null | undefined) {
  return (
    <StatusTag tone={uploadEventTone(kind, attribution)} dot>
      {uploadKindLabel(kind)}
    </StatusTag>
  );
}

/** 帰属。確定は素の札、推定は「どのサービス宛てか決まらない」ことの印 */
export function uploadAttributionTag(v: string | null | undefined) {
  if (!v) return "";
  return <Tag color={v === "ambiguous" ? "default" : "blue"}>{uploadAttributionLabel(v)}</Tag>;
}

export function uploadActionTag(v: string) {
  return <StatusTag tone={uploadActionTone(v)}>{uploadActionLabel(v)}</StatusTag>;
}

/** 端末のアップロード観測の状態。null(capability なし・未報告)は空 */
export function uploadStateTag(state: string | null | undefined) {
  if (!state) return "";
  return (
    <StatusTag tone={uploadStateTone(state)} dot>
      {uploadStateLabel(state)}
    </StatusTag>
  );
}

export function browserPolicyTag(v: string) {
  return <StatusTag tone={browserPolicyTone(v)}>{browserPolicyLabel(v)}</StatusTag>;
}

/** 端末(ホスト名 + 製造番号)。押すと端末一覧の右パネル。 */
export function deviceCell(d: DlpDeviceBrief | null | undefined) {
  if (!d) return "";
  return (
    <div>
      <Link to={`/dlp/devices?panel=${d.id}`}>{d.hostname || d.serial}</Link>
      {d.hostname && <span className="ui-cell-sub ui-num">{d.serial}</span>}
    </div>
  );
}

/** 従業員。brief があれば従業員詳細へのリンク、匿名化済み(null)なら空。 */
export function employeeCell(e: EmployeeBrief | null | undefined) {
  if (!e) return "";
  return (
    <span>
      <Link to={`/hr/employees/${e.id}`}>{e.name || e.emp_code}</Link>{" "}
      <Typography.Text type="secondary">{e.org_unit_path}</Typography.Text>
    </span>
  );
}

/** バイト数を読める単位に。 */
export function bytesText(n: number | null | undefined): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function durationText(ms: number | null | undefined): string {
  if (ms == null) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} 秒`;
  return `${Math.floor(s / 60)} 分 ${s % 60} 秒`;
}
